import type { FC } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const COLLAPSED_MAX_HEIGHT = 600;

interface RedditPostBodyProps {
  body: string;
}

const RedditPostBody: FC<RedditPostBodyProps> = ({ body }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    setIsExpanded(false);
  }, [body]);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || isExpanded) {
      return;
    }

    setIsOverflowing(element.scrollHeight > COLLAPSED_MAX_HEIGHT + 1);
  }, [body, isExpanded]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography
        ref={textRef}
        variant="body2"
        sx={{
          lineHeight: 1.7,
          whiteSpace: 'pre-line',
          ...(!isExpanded && {
            maxHeight: COLLAPSED_MAX_HEIGHT,
            overflow: 'hidden',
          }),
        }}
      >
        {body}
      </Typography>

      {isOverflowing && !isExpanded && (
        <Box
          sx={(t) => ({
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            pt: 4,
            background: `linear-gradient(to bottom, transparent, ${t.palette.background.paper} 70%)`,
          })}
        >
          <Button
            size="small"
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            onClick={() => { setIsExpanded(true); }}
            sx={{
              minWidth: 0,
              px: 1,
              py: 0,
              textTransform: 'none',
              fontSize: '0.75rem',
              lineHeight: 1.4,
            }}
          >
            Show more
          </Button>
        </Box>
      )}

      {isExpanded && isOverflowing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
          <Button
            size="small"
            endIcon={<KeyboardArrowUpIcon sx={{ fontSize: 16 }} />}
            onClick={() => { setIsExpanded(false); }}
            sx={{
              minWidth: 0,
              px: 1,
              py: 0,
              textTransform: 'none',
              fontSize: '0.75rem',
              lineHeight: 1.4,
            }}
          >
            Show less
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RedditPostBody;
